import { DateFormat } from '@celito.clients/enums';
import {
  applyTimeOfDayPreference,
  getZonedBasedTime,
} from '@celito.clients/utils';
import { isValid, isWithinInterval, parse } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import {
  getSystemTimezone,
  TimeOfDayPrefenceEnum,
} from 'libs/core/src/enums/date-formats';
import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';

import { DatePickerProps, IDatePickerStrings } from './date-picker.model';
import DatePickerView from './date-picker.view';

const defaultStrings: Partial<IDatePickerStrings> = {
  invalidInputErrorMessage: 'Invalid date format',
  isOutOfBoundsErrorMessage: 'Date is out of bounds',
};

const DatePickerController = (props: DatePickerProps) => {
  const { minDate, maxDate, value, ...rest } = props;
  const [textInputError, setTextInputError] = useState<FieldError | undefined>(
    undefined
  );
  const strings = { ...defaultStrings, ...props.strings };

  let minDateZoned: Date | undefined;
  let maxDateZoned: Date | undefined;

  if (minDate) {
    minDateZoned = getZonedBasedTime(minDate);
    minDateZoned?.setHours(0, 0, 0, 0);
  }

  if (maxDate) {
    maxDateZoned = getZonedBasedTime(maxDate);
    maxDateZoned?.setHours(23, 59, 59, 999);
  }

  const getValueInTimezone = (value: unknown): Date | undefined => {
    if (typeof value === 'string' || value instanceof Date) {
      return getZonedBasedTime(value);
    }
    return undefined;
  };

  const onSelectDateHandler = (date: Date | null | undefined): void => {
    if (!date) return;

    const dateInPreferenceTimeOfDay = applyTimeOfDayPreference(
      date,
      props?.timeOfDayPreference ?? TimeOfDayPrefenceEnum.START
    );

    const zonedDate = fromZonedTime(
      dateInPreferenceTimeOfDay,
      getSystemTimezone()
    );

    props.onDateChange(zonedDate);
    setTextInputError(undefined);
  };

  const validateInput = (
    inputValue: string
  ): { parsedDate: Date | null; errorType?: string } => {
    if (!inputValue) {
      return { parsedDate: null };
    }

    const referenceDate = getZonedBasedTime(new Date());
    const newDate = parse(inputValue, DateFormat.Date, referenceDate);

    if (!isValid(newDate)) {
      return { parsedDate: null, errorType: 'format' };
    }

    const dateInPreferenceTimeOfDay = applyTimeOfDayPreference(
      newDate,
      props?.timeOfDayPreference ?? TimeOfDayPrefenceEnum.START
    );

    if (minDateZoned && maxDateZoned) {
      if (
        !isWithinInterval(dateInPreferenceTimeOfDay, {
          start: minDateZoned,
          end: maxDateZoned,
        })
      ) {
        return { parsedDate: null, errorType: 'outOfBounds' };
      }
    }
    if (minDateZoned) {
      if (dateInPreferenceTimeOfDay < minDateZoned) {
        return { parsedDate: null, errorType: 'outOfBounds' };
      }
    }
    if (maxDateZoned) {
      if (dateInPreferenceTimeOfDay > maxDateZoned) {
        return { parsedDate: null, errorType: 'outOfBounds' };
      }
    }

    return { parsedDate: dateInPreferenceTimeOfDay };
  };

  // Validation function that can be called from multiple event handlers
  const validateCurrentInput = (inputValue: string): void => {
    if (!inputValue) {
      setTextInputError(undefined);
      return;
    }

    // Validate the input
    const validation = validateInput(inputValue);

    if (validation.parsedDate) {
      setTextInputError(undefined);
    } else {
      // Use the appropriate error message based on the error type
      const errorMessage =
        validation.errorType === 'outOfBounds'
          ? strings.isOutOfBoundsErrorMessage
          : strings.invalidInputErrorMessage;

      setTextInputError({
        type: 'validation',
        message: errorMessage,
      });
    }
  };

  // Handle the blur event for validation
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const inputElement = event.target;
    const inputValue = inputElement.value;
    validateCurrentInput(inputValue);
  };

  // Handle input changes to clear errors when typing
  const handleInputChange = (event: React.FormEvent<HTMLElement>): void => {
    // Clear any existing errors as soon as the user starts typing again
    if (textInputError) {
      setTextInputError(undefined);
    }
    // Forward to the original handler if provided
    if (props.onInputChange) {
      props.onInputChange(event);
    }
  };

  return (
    <DatePickerView
      onSelectDateHandler={onSelectDateHandler}
      onInputBlur={handleBlur}
      onInputChange={handleInputChange}
      textInputError={textInputError}
      minDate={minDateZoned}
      maxDate={maxDateZoned}
      value={getValueInTimezone(value)}
      {...rest}
    />
  );
};

export default DatePickerController;
